import classNames from "classnames";
import * as React from "react";
import { Link, LinkProps } from "react-router-dom";
import { AspectRatio } from "./AspectRatio";
import { SvgSprite } from "./SvgSprite";

import { StrapiArticle } from "../types";

interface ArticleCardProps<AsAnchor extends boolean>
  extends Pick<StrapiArticle, "title" | "summary" | "type"> {
  asAnchor?: AsAnchor;
  to: AsAnchor extends false ? LinkProps["to"] : string;
  className?: string;
  wrapperClassName?: string;

  imgUrl1x?: string;
  imgUrl2x?: string;
}

export const ArticleCard = <AsAnchor extends boolean = false>(
  props: ArticleCardProps<AsAnchor>
): JSX.Element => {
  const {
    title,
    summary,
    to,
    type,
    imgUrl1x,
    imgUrl2x,
    className,
    asAnchor,
    wrapperClassName = "mb-5",
  } = props;

  return (
    <div
      css={`
        scroll-snap-coordinate: 50% 50%;
        scroll-snap-align: center;
      `}
      className={classNames("w-auto col-md-4", wrapperClassName)}
    >
      <article
        css={`
          @media (max-width: ${(p): string => p.theme.mdBreakpoint}) {
            width: 280px;
          }
        `}
        className={classNames("card h-100", className)}
      >
        {imgUrl1x && imgUrl2x && (
          <div className="card-img-top overflow-hidden position-relative">
            <AspectRatio width={290} height={180}>
              <picture>
                <source srcSet={[imgUrl1x, `${imgUrl2x} 2x`].join(", ")} />
                <img
                  css={`
                    object-fit: cover;
                    object-position: 50% 50%;
                  `}
                  src={imgUrl2x}
                  alt=""
                />
              </picture>
            </AspectRatio>
            {type === "video" && (
              <span
                className="badge badge-primary px-3"
                css={`
                  position: absolute;
                  bottom: 1rem;
                  right: 1rem;
                `}
              >
                Video
              </span>
            )}
          </div>
        )}

        <div className="card-body d-flex flex-column">
          <h3 className="h5 card-title">{title}</h3>
          <p className="card-text">{summary}</p>
          {asAnchor === true ? (
            <a
              className={classNames("stretched-link mt-auto text-right", {
                "text-white focus-light": !!className,
              })}
              aria-label={
                type === "video"
                  ? `Watch video on ${title}`
                  : `Read article on ${title}`
              }
              href={to as string}
            >
              {type === "video" ? "Watch" : "Read"}
              <SvgSprite width={16} height={16} id="icons:chev-right" />
            </a>
          ) : (
            <Link
              className={classNames("stretched-link mt-auto text-right", {
                "text-white focus-light": !!className,
              })}
              aria-label={
                type === "video"
                  ? `Watch video on ${title}`
                  : `Read article on ${title}`
              }
              to={to}
            >
              {type === "video" ? "Watch" : "Read"}
              <SvgSprite width={16} height={16} id="icons:chev-right" />
            </Link>
          )}
        </div>
      </article>
    </div>
  );
};

type ImgProps = React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>;

interface ArticleFeaturedCardProps<AsAnchor extends boolean>
  extends Omit<ArticleCardProps<AsAnchor>, "imgUrl1x" | "imgUrl2x" | "title"> {
  headline: string;

  desktopImgUrl1x?: string;
  desktopImgUrl2x?: string;

  mobileImgUrl1x?: string;
  mobileImgUrl2x?: string;

  imgProps?: Omit<ImgProps, "alt" | "src" | "css">;
}

export const ArticleFeaturedCard = <AsAnchor extends boolean = false>({
  headline,
  summary,
  desktopImgUrl1x,
  desktopImgUrl2x,
  mobileImgUrl1x,
  mobileImgUrl2x,
  imgProps,
  to,
  asAnchor,
  wrapperClassName = "mb-5",
}: ArticleFeaturedCardProps<AsAnchor>): JSX.Element => {
  const areImagesProvided = !!(
    desktopImgUrl1x &&
    desktopImgUrl2x &&
    mobileImgUrl1x &&
    mobileImgUrl2x
  );

  return (
    <article
      className={classNames(
        "card overflow-hidden bg-secondary text-white",
        wrapperClassName
      )}
    >
      <div className="row no-gutters">
        {areImagesProvided && (
          <div className="position-relative col-lg-7">
            <picture>
              <source
                media={"(min-width: 992px)"}
                srcSet={[desktopImgUrl1x, `${desktopImgUrl2x} 2x`].join(", ")}
              />
              <source
                srcSet={[mobileImgUrl1x, `${mobileImgUrl2x} 2x`].join(", ")}
              />
              <img
                css={`
                  object-fit: cover;
                  object-position: 50% 50%;
                  min-width: 100%;

                  @media (min-width: ${(p): string => p.theme.lgBreakpoint}) {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                  }
                `}
                src={mobileImgUrl2x}
                alt=""
                {...imgProps}
              />
            </picture>
          </div>
        )}
        <div
          className={classNames("position-static", {
            "col-lg-5": areImagesProvided,
          })}
        >
          <div className="card-body px-md-4 py-md-5 h-100 d-flex flex-column">
            <h2 className="h4 card-title">{headline}</h2>
            <p className="card-text">{summary}</p>
            {asAnchor === true ? (
              <a
                className="text-white stretched-link mt-auto text-right focus-light"
                aria-label={`Read article on ${headline}`}
                href={to as string}
              >
                Read
                <SvgSprite width={16} height={16} id="icons:chev-right" />
              </a>
            ) : (
              <Link
                className="text-white stretched-link mt-auto text-right focus-light"
                aria-label={`Read article on ${headline}`}
                to={to}
              >
                Read
                <SvgSprite width={16} height={16} id="icons:chev-right" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};
